import { useEffect, useRef } from "react";

const PRIVACY_CONTACT_EMAIL = process.env.REACT_APP_PRIVACY_CONTACT_EMAIL || "privacy@inksolver.com";

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

export default function PrivacyPolicyOverlay({ onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="privacy-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <article
        className="privacy-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacy-title"
        aria-describedby="privacy-introduction"
      >
        <header className="privacy-dialog-header">
          <div>
            <span className="privacy-dialog-kicker">INKSOLVER</span>
            <h1 id="privacy-title">Privacy Policy</h1>
            <p>Effective August 27, 2026</p>
          </div>
          <button
            ref={closeButtonRef}
            className="privacy-close-button"
            type="button"
            aria-label="Close Privacy Policy"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>

        <div className="privacy-dialog-body">
          <p id="privacy-introduction" className="privacy-introduction">
            InkSolver is an ink-driven mathematics learning platform. This policy explains what information
            we process, why we process it, and the choices available to students, parents, guardians, and other users.
          </p>

          <section>
            <h2>1. Information we collect</h2>
            <p>We collect information that you provide or create while using InkSolver, including:</p>
            <ul>
              <li>Account and profile information, such as your email address, name, username, profile image, and sign-in provider.</li>
              <li>Learning information, such as selected questions, answers, attempts, progress, tutor interactions, hints, and analysis results.</li>
              <li>Slate information, including strokes, handwritten working, diagrams, OCR results, and edits made in the Math Editor.</li>
              <li>Collaboration information, including room membership, shared ink, game activity, and connection metadata. Voice is transmitted in real time to participants and is not intentionally recorded by InkSolver.</li>
              <li>Technical information, such as IP address, browser and device details, diagnostic logs, and identifiers stored in cookies or local storage.</li>
            </ul>
          </section>

          <section>
            <h2>2. How we use information</h2>
            <p>We use this information to:</p>
            <ul>
              <li>Create and secure your account, preserve your work, and provide InkSolver's features.</li>
              <li>Recognize handwritten mathematics, generate questions, provide step-aware tutoring, and show learning progress.</li>
              <li>Enable shared rooms, notifications, and other collaboration features you choose to use.</li>
              <li>Operate, troubleshoot, protect, and improve the platform and its learning experience.</li>
              <li>Send account, security, product, and learning communications. You can opt out of non-essential emails.</li>
            </ul>
          </section>

          <section>
            <h2>3. Handwriting and AI</h2>
            <p>
              InkSolver uses OCR and AI-assisted systems to interpret submitted work and provide educational feedback.
              These systems can make mistakes. Their output is guidance, not a substitute for a teacher's or guardian's judgement.
              We do not use your private slate content for advertising.
            </p>
          </section>

          <section>
            <h2>4. When information is shared</h2>
            <p>
              We share information only as needed to run InkSolver: with infrastructure, authentication, storage,
              communications, analytics, and AI service providers; with people you deliberately join in a collaboration
              room; or when required for safety, security, or law. We do not sell personal information.
            </p>
          </section>

          <section>
            <h2>5. Children and student accounts</h2>
            <p>
              InkSolver is designed for students. A child must not independently create an account where applicable law
              requires a parent or guardian to provide verifiable consent. Parents and guardians should supervise account
              creation and use. InkSolver may introduce age and guardian verification steps where required. We do not use
              children's data for targeted advertising or tracking that is harmful to their well-being.
            </p>
          </section>

          <section>
            <h2>6. Retention and security</h2>
            <p>
              We retain information while it is needed to provide the service, maintain legitimate learning records,
              resolve disputes, secure the platform, and meet legal obligations. We use administrative and technical
              safeguards, but no online service can guarantee absolute security. Deleted information may remain temporarily
              in protected backups before being removed through normal retention cycles.
            </p>
          </section>

          <section>
            <h2>7. Your choices and rights</h2>
            <p>
              Subject to applicable law, you may ask to access, correct, update, or erase your personal information;
              withdraw consent; or raise a privacy grievance. You can update available profile information in InkSolver.
              Withdrawing information required to operate an account may mean that some or all features can no longer be provided.
            </p>
          </section>

          <section>
            <h2>8. Changes to this policy</h2>
            <p>
              We may update this policy as InkSolver develops. If a change materially affects how we use personal information,
              we will provide an appropriate notice and request fresh consent where required.
            </p>
          </section>

          <section className="privacy-contact-section">
            <h2>9. Contact us</h2>
            <p>
              For privacy questions, requests, or grievances, email{" "}
              <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>.
            </p>
          </section>
        </div>

        <footer className="privacy-dialog-footer">
          <span>Your work stays yours.</span>
          <button type="button" onClick={onClose}>Close policy</button>
        </footer>
      </article>
    </div>
  );
}
